import { AfterViewInit, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { VirtualScrollerComponent } from '@iharbeck/ngx-virtual-scroller';
import { Role } from 'src/app/classes/auth/role.class';
import { User } from 'src/app/classes/user.class';
import { CodaltComponent } from 'src/app/codalt.component';
import { PageHeaderComponent } from 'src/app/generic/components/page-header/page-header.component';
import { AiReport, AiReportsMessage, AiReportsService } from 'src/app/services/ai-reports.service';
import { AuthorisationService } from 'src/app/services/auth/authorisation.service';
import { ConfirmDialogService } from 'src/app/services/confirm-dialog-service/confirm-dialog.service';
import { UserService } from 'src/app/services/user/user.service';
import { DAY_IN_MILLISECONDS } from 'src/app/utils.class';
import { MentionItem, UserMentionComponent } from '../user-mention/user-mention.component';

@Component({
    selector: 'app-ai-reports-chats',
    standalone: false,
    templateUrl: './ai-reports-chats.component.html',
    styleUrl: './ai-reports-chats.component.scss',
})
export class AiReportsChatsComponent extends CodaltComponent implements OnInit, AfterViewInit, OnDestroy {
    constructor(
        private aiReportsService: AiReportsService,
        private router: Router,
        private route: ActivatedRoute,
        private userService: UserService,
        private authorisationService: AuthorisationService,
        private confirmDialogService: ConfirmDialogService,
        private cdr: ChangeDetectorRef
    ) {
        super();
    }

    @ViewChild('scroll') private virtualScroller!: VirtualScrollerComponent;
    @ViewChild('userMention') private userMentionComponent!: UserMentionComponent;
    @ViewChild(PageHeaderComponent) private pageHeaderComponent!: PageHeaderComponent;

    // User mention properties
    availableUsers: User[] = [];
    availableRoles: Role[] = [];

    // Map to store @username -> {{ user: X }} and @rolename -> {{ role: X }} relationships
    mentionMap: Map<string, string> = new Map();

    showMentionDropdown = false;
    mentionQuery = '';
    mentionStartPos = -1;
    currentInput: HTMLInputElement | null = null;

    options = {
        limit: 100,
        // Week in milliseconds
        date_start: new Date(Date.now() - 7 * DAY_IN_MILLISECONDS).toISOString().split('T')[0],
        date_end: new Date().toISOString().split('T')[0],
    };

    activeChatId: number | null = 0;

    chatList: AiReport[] = [];

    messages: AiReportsMessage[] = [];

    isLoading = false;

    // Property to track which messages have their SQL queries visible (per message)
    messageQueryVisibility: Map<number, boolean> = new Map();

    // Track which messages are currently loading results to prevent duplicate requests
    private loadingResultsSet: Set<number> = new Set();

    private stickToBottom = true;

    ngOnInit(): void {
        // Load users for mention functionality
        this.subscriptions.add(
            this.userService.getList().subscribe(users => {
                this.availableUsers = users;
                this.buildMentionMap();
            })
        );

        // Load roles for mention functionality
        this.subscriptions.add(
            this.authorisationService.getRoles().subscribe(roles => {
                this.availableRoles = roles;
                this.buildMentionMap();
            })
        );

        this.loadChatList().then(() => {
            // Check for chatId in route parameters
            const chatIdParam = this.route.snapshot.params.chatId;
            if (chatIdParam) {
                const chatId = parseInt(chatIdParam, 10);
                if (!isNaN(chatId) && chatId > 0) {
                    // Check if the chat exists in the loaded chat list
                    const chatExists = this.chatList.some(chat => chat.id === chatId);
                    if (chatExists) {
                        this.selectChatWithoutNavigation(chatId);
                        return;
                    }
                }
            }
            // If no valid chatId in URL or chat doesn't exist, start with empty chat
            this.selectEmptyChat();
        });

        // Subscribe to route parameter changes for browser navigation
        this.subscriptions.add(
            this.route.params.subscribe(params => {
                const chatIdParam = params.chatId;
                if (chatIdParam) {
                    const chatId = parseInt(chatIdParam, 10);
                    if (!isNaN(chatId) && chatId > 0 && chatId !== this.activeChatId) {
                        const chatExists = this.chatList.some(chat => chat.id === chatId);
                        if (chatExists) {
                            this.selectChatWithoutNavigation(chatId);
                        }
                    }
                } else if (this.activeChatId !== 0) {
                    // No chatId in route, switch to empty chat
                    this.selectChatWithoutNavigation(0);
                }
            })
        );

        // Subscribe to messages that need results loaded
        this.subscriptions.add(
            this.aiReportsService.onMessageResultsNeeded().subscribe(message => {
                if (message.ai_report_id === this.activeChatId) {
                    this.loadMessageResults(message);
                }
            })
        );
    }

    ngOnDestroy(): void {}

    ngAfterViewInit(): void {
        // Use setTimeout to ensure the virtual scroller is fully initialized
        setTimeout(() => {
            this.scrollToBottom();
        }, 100);
    }

    trackById = (_: number, m: { id: number }) => m.id;

    onSend(text: string) {
        if (!text?.trim()) {
            return;
        }

        // Convert @username mentions to structured format before sending
        const structuredText = this.convertMentionsToStructured(text);

        // For new chats (activeChatId = 0), send without ai_report_id to create new report
        const chatId = this.activeChatId === 0 ? null : this.activeChatId;

        this.subscriptions.add(
            this.aiReportsService.sendMessage(chatId, structuredText, this.options).subscribe(response => {
                // If this was a new chat, we need to update the chat list and set the active chat ID
                if (this.activeChatId === 0 && response.data.ai_report_id) {
                    this.activeChatId = response.data.ai_report_id;

                    // Update URL with new chat ID as route parameter
                    this.router.navigate(['/ai/rapporten', response.data.ai_report_id]);

                    this.loadChatList(); // Reload to get the new chat in the list
                }

                setTimeout(() => {
                    this.maybeStick();
                }, 50);
            })
        );
    }

    clearInput() {
        if (this.currentInput) {
            this.currentInput.value = '';
        }
    }

    private maybeStick() {
        if (this.stickToBottom) {
            this.scrollToBottom();
        }
    }

    private scrollToBottom() {
        if (this.virtualScroller && this.messages.length > 0) {
            const lastIndex = this.messages.length - 1;
            this.virtualScroller.scrollToIndex(lastIndex, true, 0, 0);
        }
    }

    selectChat(chatId: number) {
        this.activeChatId = chatId;

        // Update URL with chatId as route parameter
        if (chatId === 0) {
            // Navigate to base route for empty chat
            this.router.navigate(['/ai/rapporten']);
        } else {
            // Navigate to route with chatId parameter
            this.router.navigate(['/ai/rapporten', chatId]);
        }

        this.loadChatData(chatId);
    }

    // Method to select chat without navigation (used for route parameter changes)
    private selectChatWithoutNavigation(chatId: number) {
        this.activeChatId = chatId;
        this.loadChatData(chatId);
    }

    // Common method to load chat data
    private loadChatData(chatId: number) {
        // Clear loading set when switching chats
        this.loadingResultsSet.clear();

        if (chatId === 0) {
            // Empty chat, clear messages
            this.messages = [];
            return;
        }

        this.subscriptions.add(
            this.aiReportsService.getList(this.activeChatId).subscribe(response => {
                this.messages = response;

                this.messages.map(message => {
                    // tslint:disable-next-line
                    message['chat_name'] = this.chatList.find(chat => chat.id === chatId)?.title;

                    // Check if message has a SQL query and fetch results via HTTP
                    if (message.query && message.query.trim() !== '' && !message.results) {
                        this.loadMessageResults(message);
                    }
                });

                this.scrollToBottom();
            })
        );
    }

    selectEmptyChat() {
        this.selectChat(0);
    }

    private loadMessageResults(message: AiReportsMessage): void {
        // Check if results are already loaded or currently being loaded
        if (message.results || this.loadingResultsSet.has(message.id)) {
            return;
        }

        // Mark as loading
        this.loadingResultsSet.add(message.id);

        // Use default limit of 20 for initial load
        const limit = message.currentLimit || 20;

        this.subscriptions.add(
            this.aiReportsService.getMessageResults(message.id, limit).subscribe({
                next: response => {
                    // Find the message in the array and update its results
                    const messageIndex = this.messages.findIndex(m => m.id === message.id);
                    if (messageIndex !== -1) {
                        this.messages[messageIndex]['results'] = response.data.results;
                        this.messages[messageIndex]['paginatedResults'] = response.data;
                        this.messages[messageIndex]['currentLimit'] = limit;
                    }

                    // Remove from loading set
                    this.loadingResultsSet.delete(message.id);
                },
                error: error => {
                    // Remove from loading set even on error
                    this.loadingResultsSet.delete(message.id);
                },
            })
        );
    }

    /**
     * The increment value for loading more results.
     */
    private static readonly RESULTS_INCREMENT = 100;

    /**
     * Load more results for a message
     */
    loadMoreResults(message: AiReportsMessage): void {
        if (this.loadingResultsSet.has(message.id)) {
            return;
        }

        // Mark as loading
        this.loadingResultsSet.add(message.id);

        // Increase limit by RESULTS_INCREMENT
        const newLimit = (message.currentLimit || 20) + AiReportsChatsComponent.RESULTS_INCREMENT;

        this.subscriptions.add(
            this.aiReportsService.getMessageResults(message.id, newLimit).subscribe({
                next: response => {
                    // Find the message in the array and update its results
                    const messageIndex = this.messages.findIndex(m => m.id === message.id);
                    if (messageIndex !== -1) {
                        this.messages[messageIndex]['results'] = response.data.results;
                        this.messages[messageIndex]['paginatedResults'] = response.data;
                        this.messages[messageIndex]['currentLimit'] = newLimit;

                        // Force change detection
                        this.cdr.detectChanges();
                    }

                    // Remove from loading set
                    this.loadingResultsSet.delete(message.id);
                },
                error: error => {
                    // Remove from loading set even on error
                    this.loadingResultsSet.delete(message.id);
                },
            })
        );
    }

    // Context menu methods for CDK
    exportChat(chat: any) {
        if (!chat) {
            return;
        }

        const chatData = {
            title: chat.title,
            time: chat.time,
            messages: this.messages,
        };

        const dataStr = JSON.stringify(chatData, null, 2);
        const dataBlob = new Blob([dataStr], { type: 'application/json' });

        const link = document.createElement('a');
        link.href = URL.createObjectURL(dataBlob);
        link.download = `chat-${chat.title.replace(/\s+/g, '-').toLowerCase()}.json`;
        link.click();
    }

    deleteChat(chat: any) {
        if (!chat) {
            return;
        }

        // Show confirmation dialog using the confirm dialog service
        this.confirmDialogService
            .confirm({
                title: 'Gesprek verwijderen',
                text: `Weet je zeker dat je het gesprek "${chat.title}" wilt verwijderen? Deze actie kan niet ongedaan worden gemaakt.`,
                confirm: 'Verwijderen',
                cancel: 'Annuleren',
            })
            .then(() => {
                this.subscriptions.add(
                    this.aiReportsService.deleteAiReport(chat.id).subscribe({
                        next: response => {
                            this.chatList = this.chatList.filter(c => c.id !== chat.id);

                            if (this.activeChatId === chat.id) {
                                this.selectEmptyChat();
                            }
                        },
                    })
                );
            });
    }

    clearChat(chat: AiReport) {
        // Show confirmation dialog using the confirm dialog service
        this.confirmDialogService
            .confirm({
                title: 'Chat opschonen',
                text: `Weet je zeker dat je alle berichten in het gesprek "${chat.title}" wilt verwijderen? Deze actie kan niet ongedaan worden gemaakt.`,
                confirm: 'Opschonen',
                cancel: 'Annuleren',
            })
            .then(() => {
                this.subscriptions.add(
                    this.aiReportsService.clearChat(chat.id).subscribe({
                        next: () => {
                            // Clear messages from the current view if this is the active chat
                            if (this.activeChatId === chat.id) {
                                this.messages = [];
                            }

                            // Optionally reload the chat list to update any message counts or timestamps
                            this.loadChatList();
                        },
                    })
                );
            });
    }

    private loadChatList(): Promise<void> {
        return new Promise(resolve => {
            this.subscriptions.add(
                this.aiReportsService.getAiReports().subscribe(chats => {
                    this.chatList = chats.data;
                    resolve();
                })
            );
        });
    }

    // Build the mention map for @username -> {{ user: X }} and @rolename -> {{ role: X }} conversion
    private buildMentionMap() {
        this.mentionMap.clear();

        // Add user mentions
        this.availableUsers.forEach(user => {
            const atUsername = `@${user.name}`;
            const mentionText = `{{ user: ${user.id} }}`;
            this.mentionMap.set(atUsername, mentionText);
        });

        // Add role mentions
        this.availableRoles.forEach(role => {
            const atRoleName = `@${role.name}`;
            const mentionText = `{{ role: "${role.key}" }}`;
            this.mentionMap.set(atRoleName, mentionText);
        });
    }

    // Convert @username mentions to {{ user: X }} format using the mention map
    private convertMentionsToStructured(text: string): string {
        let structuredText = text;

        // Iterate through all entries in the mention map
        this.mentionMap.forEach((mentionValue, atUsername) => {
            // Replace all occurrences of @username with {{ user: X }}
            structuredText = structuredText.replace(new RegExp(this.escapeRegex(atUsername), 'g'), mentionValue);
        });

        return structuredText;
    } // Helper method to escape special regex characters
    private escapeRegex(string: string): string {
        return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    }

    // User mention methods
    onInputChange(event: Event) {
        const input = event.target as HTMLInputElement;
        const text = input.value;
        const cursorPos = input.selectionStart || 0;

        this.currentInput = input;

        // Check for @ mentions
        this.checkForMention(text, cursorPos);
    }

    onKeyDown(event: KeyboardEvent) {
        if (this.showMentionDropdown) {
            switch (event.key) {
                case 'ArrowUp':
                    event.preventDefault();
                    this.userMentionComponent.navigateUp();
                    break;
                case 'ArrowDown':
                    event.preventDefault();
                    this.userMentionComponent.navigateDown();
                    break;
                case 'Enter':
                case 'Tab':
                    event.preventDefault();
                    this.userMentionComponent.selectCurrent();
                    break;
                case 'Escape':
                    event.preventDefault();
                    this.closeMentionDropdown();
                    break;
            }
        }
    }

    private checkForMention(text: string, cursorPos: number) {
        const beforeCursor = text.substring(0, cursorPos);
        const atIndex = beforeCursor.lastIndexOf('@');

        if (atIndex === -1) {
            this.closeMentionDropdown();
            return;
        }

        const afterAt = beforeCursor.substring(atIndex + 1);

        // Check if there's a space before the @ (or it's at the start)
        if (atIndex > 0 && beforeCursor[atIndex - 1] !== ' ') {
            this.closeMentionDropdown();
            return;
        }

        // Check if there's no space in the text after @
        if (afterAt.includes(' ')) {
            this.closeMentionDropdown();
            return;
        }

        // Show mention dropdown
        this.mentionStartPos = atIndex;
        this.mentionQuery = afterAt;
        this.showMentionDropdown = true;
    }

    onItemMentioned(item: MentionItem) {
        if (this.currentInput && this.mentionStartPos !== -1) {
            const currentText = this.currentInput.value;
            const cursorPos = this.currentInput.selectionStart || 0;

            // Find the end of the current mention being typed
            const beforeMention = currentText.substring(0, this.mentionStartPos);
            const afterMentionStart = currentText.substring(this.mentionStartPos + 1); // +1 to skip the @
            const spaceIndex = afterMentionStart.indexOf(' ');
            const mentionEnd = spaceIndex === -1 ? currentText.length : this.mentionStartPos + 1 + spaceIndex;

            // Get text after the mention
            const afterMention = currentText.substring(mentionEnd);

            // Insert @name format (will be converted to structured format when sending)
            const mentionText = `@${item.name}`;
            const newText = `${beforeMention}${mentionText}${afterMention}`;

            // Smart spacing - add space if there's more text after and it doesn't start with space
            const needsSpace = afterMention.trim().length > 0 && !afterMention.startsWith(' ');
            const finalText = needsSpace ? `${beforeMention}${mentionText} ${afterMention}` : newText;

            // Update the input field
            this.currentInput.value = finalText;

            // Set cursor position after the mention
            const newCursorPos = beforeMention.length + mentionText.length + (needsSpace ? 1 : 0);
            setTimeout(() => {
                this.currentInput?.setSelectionRange(newCursorPos, newCursorPos);
                this.currentInput?.focus();
            }, 0);
        }

        this.closeMentionDropdown();
    }

    private closeMentionDropdown() {
        this.showMentionDropdown = false;
        this.mentionQuery = '';
        this.mentionStartPos = -1;
    }

    // Handle project number clicks from child component
    onProjectClicked(projectNumber: string): void {
        this.pageHeaderComponent.settingsMenuComponent.prefillSearch(projectNumber);
    }
}
